// src/components/admin/AdminUserManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { adminUserService } from '../../services/adminUserService';
import { organizationService } from '../../services/organizationService';
import EditAdminModal from './EditAdminModal';
import LoadingSpinner from '../common/LoadingSpinner';
import Pagination from '../common/Pagination';
import Toast from '../common/Toast';
import EmailVerificationBadge from '../common/EmailVerificationBadge';
import Modal from '../common/Modal';
import SearchableSelect from '../common/SearchableSelect';
import { usePageTitle } from '../../hooks/usePageTitle';

const AdminUserManagement = () => {
    usePageTitle('Admin User Management');
    const { user } = useAuth();

    const [adminUsers, setAdminUsers] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [saveLoading, setSaveLoading] = useState(false);
    const [showCreateDelegateModal, setShowCreateDelegateModal] = useState(false);
    const [delegateMetaLoading, setDelegateMetaLoading] = useState(false);
    const [delegateMeta, setDelegateMeta] = useState({ permissions_by_module: {}, title_templates: [] });
    const [delegateFormLoading, setDelegateFormLoading] = useState(false);
    const [delegateForm, setDelegateForm] = useState({
        username: '',
        name: '',
        email: '',
        password: '',
        delegate_title: 'Reviewer',
        permission_ids: []
    });

    const [showEditPermissionsModal, setShowEditPermissionsModal] = useState(false);
    const [editPermissionsUser, setEditPermissionsUser] = useState(null);
    const [editPermissionsMetaLoading, setEditPermissionsMetaLoading] = useState(false);
    const [editPermissionsMeta, setEditPermissionsMeta] = useState({ permissions_by_module: {}, title_templates: [] });
    const [editPermissionsFormLoading, setEditPermissionsFormLoading] = useState(false);
    const [editPermissionsForm, setEditPermissionsForm] = useState({
        delegate_title: '',
        permission_ids: []
    });

    // Filters
    const [filters, setFilters] = useState({
        search: '',
        user_type: '',
        org_id: '',
        is_active: '',
        page: 1,
        limit: 20
    });

    // Pagination
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 20,
        total: 0,
        total_pages: 1
    });

    // Toast
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    // Summary cards
    const [adminStats, setAdminStats] = useState({ client_admin: 0, core_delegate: 0, pending: 0, verified: 0 });

    const organizationOptions = organizations.map((org) => ({
        id: org.id?.toString(),
        name: org.name
    }));

    // Load organizations for core_admin filter
    const loadOrganizations = useCallback(async () => {
        if (user?.user_type !== 'core_admin') return;

        try {
            const response = await organizationService.getOrganizations({ page: 1, limit: 1000 });
            // Response structure: { organizations: [...], pagination: {...} }
            if (response.organizations) {
                setOrganizations(response.organizations);
            }
        } catch (err) {
            console.error('Error loading organizations:', err);
        }
    }, [user]);

    // Load organizations on mount for core_admin
    useEffect(() => {
        if (user?.user_type === 'core_admin') {
            loadOrganizations();
        }
    }, [user, loadOrganizations]);

    // Load admin users
    const loadAdminUsers = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const params = {};

            // Add search filter
            if (filters.search) {
                params.search = filters.search;
            }

            // Add user type filter
            if (filters.user_type) {
                params.user_type = filters.user_type;
            }

            // Add organization filter
            if (filters.org_id) {
                params.org_id = filters.org_id;
            }

            // Add active status filter
            if (filters.is_active !== '') {
                params.is_active = filters.is_active;
            }

            // Add pagination
            params.page = filters.page;
            params.limit = filters.limit;

            const response = await adminUserService.getAdminUsers(params);

            // Response structure: { status, message, data: { users, pagination } }
            // adminUserService returns response.data, so we get { users, pagination }
            if (response.users) {
                setAdminUsers(response.users || []);

                if (response.pagination) {
                    setPagination(response.pagination);
                }
            } else {
                throw new Error('Failed to load admin users');
            }
        } catch (err) {
            console.error('Error loading admin users:', err);
            setError(err.message);
            showToast(err.message, 'error');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    // Load summary cards - scoped by organization like the list itself, but
    // independent of the list's own search/type/status filters so the cards
    // stay a stable at-a-glance summary rather than re-totaling on every
    // keystroke.
    const loadAdminStats = useCallback(async () => {
        try {
            const params = {};
            if (filters.org_id) {
                params.org_id = filters.org_id;
            }
            const response = await adminUserService.getAdminUserStats(params);
            setAdminStats(response || { client_admin: 0, core_delegate: 0, pending: 0, verified: 0 });
        } catch (err) {
            console.error('Error loading admin user stats:', err);
        }
    }, [filters.org_id]);

    // Load on mount and filter changes
    useEffect(() => {
        if (user) {
            loadAdminUsers();
            loadAdminStats();
        }
    }, [user, loadAdminUsers, loadAdminStats]);

    // Handle edit click
    const handleEditClick = (adminUser) => {
        setEditingUser(adminUser);
        setShowEditModal(true);
    };

    const [revokingUserId, setRevokingUserId] = useState(null);

    const handleRevokeDelegate = async (adminUser) => {
        if (!window.confirm(
            `Revoke the "${adminUser.delegate_title || 'Delegate'}" role from ${adminUser.name || adminUser.username}?\n\n` +
            `This removes all their assigned permissions and deactivates their account. This cannot be undone from here.`
        )) {
            return;
        }

        try {
            setRevokingUserId(adminUser.id);
            await adminUserService.revokeCoreDelegate(adminUser.id);
            showToast('Delegate role revoked and account deactivated', 'success');
            await loadAdminUsers();
            await loadAdminStats();
        } catch (err) {
            showToast(err.message || 'Failed to revoke delegate role', 'error');
        } finally {
            setRevokingUserId(null);
        }
    };

    const openEditPermissionsModal = async (adminUser) => {
        setEditPermissionsUser(adminUser);
        setShowEditPermissionsModal(true);
        setEditPermissionsMetaLoading(true);
        try {
            const [meta, detail] = await Promise.all([
                adminUserService.getCoreDelegateMeta(),
                adminUserService.getCoreDelegateDetail(adminUser.id)
            ]);
            setEditPermissionsMeta(meta || { permissions_by_module: {}, title_templates: [] });
            setEditPermissionsForm({
                delegate_title: detail?.delegate_title || adminUser.delegate_title || '',
                permission_ids: detail?.permission_ids || []
            });
        } catch (err) {
            showToast(err.message || 'Failed to load delegate permissions', 'error');
            setShowEditPermissionsModal(false);
        } finally {
            setEditPermissionsMetaLoading(false);
        }
    };

    const toggleEditPermission = (permissionId) => {
        setEditPermissionsForm(prev => {
            const selected = new Set(prev.permission_ids);
            if (selected.has(permissionId)) {
                selected.delete(permissionId);
            } else {
                selected.add(permissionId);
            }
            return { ...prev, permission_ids: Array.from(selected) };
        });
    };

    const handleSavePermissions = async (e) => {
        e.preventDefault();
        if (editPermissionsForm.permission_ids.length === 0) {
            showToast('Select at least one module permission', 'error');
            return;
        }

        try {
            setEditPermissionsFormLoading(true);
            await adminUserService.updateCoreDelegatePermissions(editPermissionsUser.id, editPermissionsForm);
            showToast('Delegate permissions updated successfully', 'success');
            setShowEditPermissionsModal(false);
            setEditPermissionsUser(null);
            await loadAdminUsers();
            await loadAdminStats();
        } catch (err) {
            showToast(err.message || 'Failed to update delegate permissions', 'error');
        } finally {
            setEditPermissionsFormLoading(false);
        }
    };

    const openCreateDelegateModal = async () => {
        try {
            setShowCreateDelegateModal(true);
            setDelegateMetaLoading(true);
            const meta = await adminUserService.getCoreDelegateMeta();
            setDelegateMeta(meta || { permissions_by_module: {}, title_templates: [] });
        } catch (err) {
            showToast(err.message || 'Failed to load delegate permissions', 'error');
            setShowCreateDelegateModal(false);
        } finally {
            setDelegateMetaLoading(false);
        }
    };

    const toggleDelegatePermission = (permissionId) => {
        setDelegateForm(prev => {
            const selected = new Set(prev.permission_ids);
            if (selected.has(permissionId)) {
                selected.delete(permissionId);
            } else {
                selected.add(permissionId);
            }
            return { ...prev, permission_ids: Array.from(selected) };
        });
    };

    const handleCreateDelegate = async (e) => {
        e.preventDefault();
        if (delegateForm.permission_ids.length === 0) {
            showToast('Select at least one module permission', 'error');
            return;
        }

        try {
            setDelegateFormLoading(true);
            await adminUserService.createCoreDelegate(delegateForm);
            showToast('Core delegate created successfully', 'success');
            setShowCreateDelegateModal(false);
            setDelegateForm({
                username: '',
                name: '',
                email: '',
                password: '',
                delegate_title: 'Reviewer',
                permission_ids: []
            });
            await loadAdminUsers();
            await loadAdminStats();
        } catch (err) {
            showToast(err.message || 'Failed to create core delegate', 'error');
        } finally {
            setDelegateFormLoading(false);
        }
    };

    // Handle save
    const handleSave = async (userData) => {
        try {
            setSaveLoading(true);

            const response = await adminUserService.updateAdminUser(editingUser.id, userData);

            // Response structure: { status, message, data: { user, message } }
            // adminUserService returns response.data
            if (response.user) {
                showToast(response.message || 'Admin user updated successfully', 'success');
                setShowEditModal(false);
                setEditingUser(null);

                // Reload the list
                await loadAdminUsers();
                await loadAdminStats();
            } else {
                throw new Error('Failed to update admin user');
            }
        } catch (err) {
            console.error('Error updating admin user:', err);
            showToast(err.message, 'error');
        } finally {
            setSaveLoading(false);
        }
    };

    // Handle filter change
    const handleFilterChange = (name, value) => {
        setFilters(prev => ({
            ...prev,
            [name]: value,
            page: 1 // Reset to first page on filter change
        }));
    };

    // Handle page change
    const handlePageChange = (page) => {
        setFilters(prev => ({
            ...prev,
            page: page
        }));
    };

    // Handle search
    const handleSearch = (e) => {
        const value = e.target.value;
        handleFilterChange('search', value);
    };

    // Show toast
    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
    };

    // Hide toast
    const hideToast = () => {
        setToast({ show: false, message: '', type: 'success' });
    };

    // Check if user can change status (core_admin only)
    const canChangeStatus = user?.user_type === 'core_admin';
    const userTableHeaderStyle = {
        padding: '12px',
        textAlign: 'left',
        fontWeight: '600',
        position: 'sticky',
        top: 0,
        zIndex: 2,
        backgroundColor: '#f8f9fa'
    };

    const userTableHeaderCenterStyle = {
        ...userTableHeaderStyle,
        textAlign: 'center'
    };

    return (
        <div
            className="admin-user-management"
            style={{
                padding: '20px',
                maxWidth: '1400px',
                margin: '0 auto',
                width: '100%',
                boxSizing: 'border-box'
            }}
        >
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '30px'
            }}>
                <div>
                    <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 'bold', color: '#333' }}>
                        Admin User Management
                    </h1>
                    <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: '14px' }}>
                        Manage client admins and core admins (users not linked to staff)
                    </p>
                </div>
                {user?.user_type === 'core_admin' && user?.role !== 'core_delegate' && (
                    <button
                        onClick={openCreateDelegateModal}
                        style={{
                            padding: '10px 16px',
                            backgroundColor: '#0f766e',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 600
                        }}
                    >
                        + Create Core Delegate
                    </button>
                )}
            </div>

            {/* Summary Cards */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '16px',
                marginBottom: '20px'
            }}>
                {[
                    { label: 'Client Admins', value: adminStats.client_admin, icon: 'fa-user-tie', color: '#007bff' },
                    { label: 'Core Delegates', value: adminStats.core_delegate, icon: 'fa-user-shield', color: '#7c3aed' },
                    { label: 'Pending Verification', value: adminStats.pending, icon: 'fa-hourglass-half', color: '#f59e0b' },
                    { label: 'Verified', value: adminStats.verified, icon: 'fa-check-circle', color: '#28a745' }
                ].map((card) => (
                    <div
                        key={card.label}
                        style={{
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            padding: '18px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '14px'
                        }}
                    >
                        <div style={{
                            width: '44px',
                            height: '44px',
                            borderRadius: '10px',
                            backgroundColor: `${card.color}1a`,
                            color: card.color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '18px',
                            flexShrink: 0
                        }}>
                            <i className={`fas ${card.icon}`}></i>
                        </div>
                        <div>
                            <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#333', lineHeight: 1.2 }}>
                                {card.value}
                            </div>
                            <div style={{ fontSize: '13px', color: '#666' }}>{card.label}</div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Filters */}
            <div style={{
                backgroundColor: '#fff',
                padding: '20px',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                marginBottom: '20px'
            }}>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '15px'
                }}>
                    {/* Search */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>
                            Search
                        </label>
                        <input
                            type="text"
                            placeholder="Username, email, or name..."
                            value={filters.search}
                            onChange={handleSearch}
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px'
                            }}
                        />
                    </div>

                    {/* User Type Filter */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>
                            User Type
                        </label>
                        <select
                            value={filters.user_type}
                            onChange={(e) => handleFilterChange('user_type', e.target.value)}
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px'
                            }}
                        >
                            <option value="">All Types</option>
                            {user?.user_type === 'core_admin' && (
                                <option value="core_admin">Core Admin</option>
                            )}
                            <option value="client_admin">Client Admin</option>
                        </select>
                    </div>

                    {/* Organization Filter - Core Admin Only */}
                    {user?.user_type === 'core_admin' && (
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>
                                Organization
                            </label>
                            <SearchableSelect
                                value={filters.org_id}
                                onChange={(value) => handleFilterChange('org_id', value || '')}
                                options={organizationOptions}
                                placeholder="All Organizations"
                                displayKey="name"
                                valueKey="id"
                                searchPlaceholder="Search organizations..."
                            />
                        </div>
                    )}

                    {/* Active Status Filter */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '500' }}>
                            Status
                        </label>
                        <select
                            value={filters.is_active}
                            onChange={(e) => handleFilterChange('is_active', e.target.value)}
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px'
                            }}
                        >
                            <option value="">All Status</option>
                            <option value="1">Active</option>
                            <option value="0">Disabled</option>
                        </select>
                    </div>

                    {/* Clear Filters */}
                    <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                        <button
                            onClick={() => setFilters({
                                search: '',
                                user_type: '',
                                org_id: '',
                                is_active: '',
                                page: 1,
                                limit: 20
                            })}
                            style={{
                                padding: '8px 16px',
                                backgroundColor: '#6c757d',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                width: '100%'
                            }}
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>
            </div>

            {/* Loading State */}
            {loading && (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <LoadingSpinner />
                    <p style={{ marginTop: '10px', color: '#666' }}>Loading admin users...</p>
                </div>
            )}

            {/* Error State */}
            {!loading && error && (
                <div style={{
                    backgroundColor: '#f8d7da',
                    border: '1px solid #f5c6cb',
                    borderRadius: '4px',
                    padding: '20px',
                    color: '#721c24',
                    textAlign: 'center'
                }}>
                    <p style={{ margin: 0 }}>{error}</p>
                    <button
                        onClick={loadAdminUsers}
                        style={{
                            marginTop: '15px',
                            padding: '8px 20px',
                            backgroundColor: '#dc3545',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Retry
                    </button>
                </div>
            )}

            {/* Admin Users Table */}
            {!loading && !error && (
                <>
                    <div style={{
                        backgroundColor: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        overflow: 'hidden'
                    }}>
                        {adminUsers.length === 0 ? (
                            <div style={{
                                padding: '60px 20px',
                                textAlign: 'center',
                                color: '#666'
                            }}>
                                <svg
                                    width="64"
                                    height="64"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1"
                                    style={{ margin: '0 auto 20px', opacity: 0.3 }}
                                >
                                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                    <circle cx="8.5" cy="7" r="4" />
                                    <line x1="20" y1="8" x2="20" y2="14" />
                                    <line x1="23" y1="11" x2="17" y2="11" />
                                </svg>
                                <h3 style={{ marginBottom: '10px' }}>No admin users found</h3>
                                <p>Try adjusting your filters or search criteria</p>
                            </div>
                        ) : (
                            <div style={{
                                maxHeight: 'min(70vh, 720px)',
                                overflow: 'auto',
                                scrollbarGutter: 'stable both-edges'
                            }}>
                                <table style={{
                                    width: 'max-content',
                                    minWidth: '1200px',
                                    borderCollapse: 'collapse',
                                    fontSize: '14px'
                                }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                                            <th style={userTableHeaderStyle}>Username</th>
                                            <th style={userTableHeaderStyle}>Name</th>
                                            <th style={userTableHeaderStyle}>Email</th>
                                            <th style={userTableHeaderStyle}>Email Status</th>
                                            <th style={userTableHeaderStyle}>User Type</th>
                                            <th style={userTableHeaderStyle}>Organization</th>
                                            <th style={userTableHeaderCenterStyle}>Status</th>
                                            <th style={userTableHeaderCenterStyle}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {adminUsers.map((adminUser) => (
                                            <tr
                                                key={adminUser.id}
                                                style={{
                                                    borderBottom: '1px solid #dee2e6',
                                                    backgroundColor: adminUser.is_active === 0 ? '#f8f9fa' : '#fff'
                                                }}
                                            >
                                                <td style={{ padding: '12px' }}>
                                                    <strong>{adminUser.username}</strong>
                                                </td>
                                                <td style={{ padding: '12px' }}>
                                                    {adminUser.name || '-'}
                                                </td>
                                                <td style={{ padding: '12px' }}>
                                                    {adminUser.email}
                                                </td>
                                                <td style={{ padding: '12px' }}>
                                                    <EmailVerificationBadge
                                                        emailVerifiedAt={adminUser.email_verified_at}
                                                        timezone={adminUser.org_timezone || 'UTC'}
                                                    />
                                                </td>
                                                <td style={{ padding: '12px' }}>
                                                    <span style={{
                                                        backgroundColor: adminUser.user_type === 'core_admin'
                                                            ? (adminUser.role === 'core_delegate' ? '#7c3aed' : '#007bff')
                                                            : '#28a745',
                                                        color: '#fff',
                                                        padding: '4px 8px',
                                                        borderRadius: '4px',
                                                        fontSize: '12px',
                                                        fontWeight: '600'
                                                    }}>
                                                        {adminUser.user_type === 'core_admin'
                                                            ? (adminUser.role === 'core_delegate' ? 'Core Delegate' : 'Core Admin')
                                                            : 'Client Admin'}
                                                    </span>
                                                    {adminUser.role === 'core_delegate' && (
                                                        <div style={{ marginTop: '6px', fontSize: '12px', color: '#555' }}>
                                                            {adminUser.delegate_title || 'Delegate'} | {adminUser.permission_count || 0} perms
                                                        </div>
                                                    )}
                                                </td>
                                                <td style={{ padding: '12px' }}>
                                                    {adminUser.org_name || '-'}
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                                    <span style={{
                                                        backgroundColor: adminUser.is_active == 1 ? '#28a745' : '#dc3545',
                                                        color: '#fff',
                                                        padding: '4px 8px',
                                                        borderRadius: '4px',
                                                        fontSize: '12px',
                                                        fontWeight: '600'
                                                    }}>
                                                        {adminUser.is_active == 1 ? 'Active' : 'Disabled'}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                                    <div style={{ display: 'inline-flex', gap: '8px' }}>
                                                        <button
                                                            onClick={() => handleEditClick(adminUser)}
                                                            style={{
                                                                padding: '6px 12px',
                                                                backgroundColor: '#007bff',
                                                                color: '#fff',
                                                                border: 'none',
                                                                borderRadius: '4px',
                                                                cursor: 'pointer',
                                                                fontSize: '13px'
                                                            }}
                                                            title="Edit admin user"
                                                        >
                                                            Edit
                                                        </button>
                                                        {adminUser.role === 'core_delegate'
                                                            && user?.user_type === 'core_admin'
                                                            && user?.role !== 'core_delegate'
                                                            && adminUser.is_active == 1 && (
                                                            <button
                                                                onClick={() => openEditPermissionsModal(adminUser)}
                                                                style={{
                                                                    padding: '6px 12px',
                                                                    backgroundColor: '#0f766e',
                                                                    color: '#fff',
                                                                    border: 'none',
                                                                    borderRadius: '4px',
                                                                    cursor: 'pointer',
                                                                    fontSize: '13px'
                                                                }}
                                                                title="Edit delegate permissions"
                                                            >
                                                                Permissions
                                                            </button>
                                                        )}
                                                        {adminUser.role === 'core_delegate'
                                                            && user?.user_type === 'core_admin'
                                                            && user?.role !== 'core_delegate'
                                                            && adminUser.is_active == 1 && (
                                                            <button
                                                                onClick={() => handleRevokeDelegate(adminUser)}
                                                                disabled={revokingUserId === adminUser.id}
                                                                style={{
                                                                    padding: '6px 12px',
                                                                    backgroundColor: revokingUserId === adminUser.id ? '#f3a5a5' : '#dc3545',
                                                                    color: '#fff',
                                                                    border: 'none',
                                                                    borderRadius: '4px',
                                                                    cursor: revokingUserId === adminUser.id ? 'not-allowed' : 'pointer',
                                                                    fontSize: '13px'
                                                                }}
                                                                title="Revoke delegate role and deactivate account"
                                                            >
                                                                {revokingUserId === adminUser.id ? 'Revoking...' : 'Revoke'}
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Pagination */}
                    {adminUsers.length > 0 && pagination.total_pages > 1 && (
                        <div style={{ marginTop: '20px' }}>
                            <Pagination
                                currentPage={pagination.current_page}
                                totalPages={pagination.total_pages}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    )}

                    {/* Stats */}
                    <div style={{
                        marginTop: '20px',
                        padding: '15px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '4px',
                        fontSize: '14px',
                        color: '#666',
                        textAlign: 'center'
                    }}>
                        Showing {adminUsers.length} of {pagination.total} admin user{pagination.total !== 1 ? 's' : ''}
                    </div>
                </>
            )}

            {/* Edit Modal */}
            {showEditModal && editingUser && (
                <EditAdminModal
                    isOpen={showEditModal}
                    onClose={() => {
                        setShowEditModal(false);
                        setEditingUser(null);
                    }}
                    adminUser={editingUser}
                    onSave={handleSave}
                    loading={saveLoading}
                    canChangeStatus={canChangeStatus}
                />
            )}

            <Modal
                isOpen={showCreateDelegateModal}
                onClose={() => setShowCreateDelegateModal(false)}
                title="Create Core Delegate"
                size="large"
            >
                {delegateMetaLoading ? (
                    <div style={{ padding: '20px', textAlign: 'center' }}>
                        <LoadingSpinner />
                        <p style={{ marginTop: '8px' }}>Loading permissions...</p>
                    </div>
                ) : (
                    <form onSubmit={handleCreateDelegate}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px' }}>
                            <div>
                                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Username</label>
                                <input
                                    type="text"
                                    required
                                    value={delegateForm.username}
                                    onChange={(e) => setDelegateForm(prev => ({ ...prev, username: e.target.value }))}
                                    style={{ width: '100%', padding: '9px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Full Name</label>
                                <input
                                    type="text"
                                    required
                                    value={delegateForm.name}
                                    onChange={(e) => setDelegateForm(prev => ({ ...prev, name: e.target.value }))}
                                    style={{ width: '100%', padding: '9px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Email</label>
                                <input
                                    type="email"
                                    required
                                    value={delegateForm.email}
                                    onChange={(e) => setDelegateForm(prev => ({ ...prev, email: e.target.value }))}
                                    style={{ width: '100%', padding: '9px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Temporary Password</label>
                                <input
                                    type="text"
                                    required
                                    value={delegateForm.password}
                                    onChange={(e) => setDelegateForm(prev => ({ ...prev, password: e.target.value }))}
                                    style={{ width: '100%', padding: '9px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                                />
                            </div>
                            <div style={{ gridColumn: '1 / -1' }}>
                                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Delegate Title</label>
                                <input
                                    type="text"
                                    list="delegate-title-templates"
                                    value={delegateForm.delegate_title}
                                    onChange={(e) => setDelegateForm(prev => ({ ...prev, delegate_title: e.target.value }))}
                                    style={{ width: '100%', padding: '9px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                                />
                                <datalist id="delegate-title-templates">
                                    {(delegateMeta.title_templates || []).map(title => (
                                        <option key={title} value={title} />
                                    ))}
                                </datalist>
                            </div>
                        </div>

                        <div style={{ marginTop: '14px' }}>
                            <div style={{ marginBottom: '8px', fontWeight: 600 }}>Portal Module Access</div>
                            <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px' }}>
                                {Object.entries(delegateMeta.permissions_by_module || {}).map(([module, modulePermissions]) => (
                                    <div key={module} style={{ marginBottom: '10px' }}>
                                        <div style={{ fontWeight: 700, marginBottom: '6px', textTransform: 'capitalize' }}>{module}</div>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px' }}>
                                            {modulePermissions.map((permission) => (
                                                <label key={permission.id} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: '13px' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={delegateForm.permission_ids.includes(permission.id)}
                                                        onChange={() => toggleDelegatePermission(permission.id)}
                                                    />
                                                    <span>
                                                        <strong>{permission.name}</strong>
                                                        <br />
                                                        <span style={{ color: '#6b7280' }}>{permission.description}</span>
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '14px' }}>
                            <button type="button" onClick={() => setShowCreateDelegateModal(false)} className="btn btn-secondary">
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={delegateFormLoading}>
                                {delegateFormLoading ? 'Creating...' : 'Create Delegate'}
                            </button>
                        </div>
                    </form>
                )}
            </Modal>

            <Modal
                isOpen={showEditPermissionsModal}
                onClose={() => {
                    setShowEditPermissionsModal(false);
                    setEditPermissionsUser(null);
                }}
                title={`Edit Permissions: ${editPermissionsUser?.name || editPermissionsUser?.username || ''}`}
                size="large"
            >
                {editPermissionsMetaLoading ? (
                    <div style={{ padding: '20px', textAlign: 'center' }}>
                        <LoadingSpinner />
                        <p style={{ marginTop: '8px' }}>Loading permissions...</p>
                    </div>
                ) : (
                    <form onSubmit={handleSavePermissions}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600 }}>Delegate Title</label>
                            <input
                                type="text"
                                list="edit-delegate-title-templates"
                                value={editPermissionsForm.delegate_title}
                                onChange={(e) => setEditPermissionsForm(prev => ({ ...prev, delegate_title: e.target.value }))}
                                style={{ width: '100%', padding: '9px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                            />
                            <datalist id="edit-delegate-title-templates">
                                {(editPermissionsMeta.title_templates || []).map(title => (
                                    <option key={title} value={title} />
                                ))}
                            </datalist>
                        </div>

                        <div style={{ marginTop: '14px' }}>
                            <div style={{ marginBottom: '8px', fontWeight: 600 }}>Portal Module Access</div>
                            <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px' }}>
                                {Object.entries(editPermissionsMeta.permissions_by_module || {}).map(([module, modulePermissions]) => (
                                    <div key={module} style={{ marginBottom: '10px' }}>
                                        <div style={{ fontWeight: 700, marginBottom: '6px', textTransform: 'capitalize' }}>{module}</div>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px' }}>
                                            {modulePermissions.map((permission) => (
                                                <label key={permission.id} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: '13px' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={editPermissionsForm.permission_ids.includes(permission.id)}
                                                        onChange={() => toggleEditPermission(permission.id)}
                                                    />
                                                    <span>
                                                        <strong>{permission.name}</strong>
                                                        <br />
                                                        <span style={{ color: '#6b7280' }}>{permission.description}</span>
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '14px' }}>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowEditPermissionsModal(false);
                                    setEditPermissionsUser(null);
                                }}
                                className="btn btn-secondary"
                            >
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={editPermissionsFormLoading}>
                                {editPermissionsFormLoading ? 'Saving...' : 'Save Permissions'}
                            </button>
                        </div>
                    </form>
                )}
            </Modal>

            {/* Toast Notifications */}
            {toast.show && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={hideToast}
                />
            )}
        </div>
    );
};

export default AdminUserManagement;
